import { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { MerchantContext } from 'commons-ui';
import { EMPTY_OBJECT, ACL_ENDPOINTS, MERCHANT_TYPES } from '../../constants';
import { getToken, getZupeeEnv, getStageEnvId } from '../../utils/token';

const axios = require('axios').default;

const DEFAULT_REQUEST_TIMEOUT = 120000;

export const createRequestHeaders = (merchantName: string) => {
	const token: any = getToken();
	const authToken = token && token.accessToken && token.accessToken.accessToken;
	const zupeeEnv = getZupeeEnv();
	const stageEnvId = getStageEnvId();
	return {
		accessToken: authToken,
		'Content-Type': 'application/json',
		userMerchant: merchantName,
		'x-zupee-env': zupeeEnv,
		'z-stage-env-id': stageEnvId
	};
};

export const cseHeaders = () => {
	const zupeeEnv = getZupeeEnv();
	const stageEnvId = getStageEnvId();
	return {
		'x-zupee-env': zupeeEnv,
		'z-stage-env-id': stageEnvId
	};
};

export const checkACLEndpoint = (endpoint: string): boolean => {
	for (let i = 0; i < ACL_ENDPOINTS.length; i += 1) {
		if (endpoint.includes(ACL_ENDPOINTS[i])) return true;
	}
	return false;
};

const useAjaxProvider = ({ baseURL, adminBaseUrl }: { baseURL: string; adminBaseUrl: string }) => {
	const history = useHistory();
	const { merchantName } = useContext(MerchantContext);

	const postData = ({
		endpoint = '/',
		queryParams = EMPTY_OBJECT,
		customHeaders = EMPTY_OBJECT,
		body = EMPTY_OBJECT,
		timeout = DEFAULT_REQUEST_TIMEOUT,
		baseUrlFromParams,
		returnResponse
	}: {
		endpoint: any;
		queryParams?: any;
		customHeaders?: any;
		body?: any;
		timeout?: any;
		baseUrlFromParams?: string;
		returnResponse?: boolean;
	}) => {
		const mandatoryHeaders = createRequestHeaders(merchantName);
		const isACLEndpoint: boolean = merchantName !== MERCHANT_TYPES.MARKETING || checkACLEndpoint(endpoint as string);

		return (
			axios({
				baseURL: isACLEndpoint ? adminBaseUrl : baseUrlFromParams || baseURL,
				method: 'POST',
				url: endpoint,
				params: queryParams,
				headers: { ...customHeaders, ...mandatoryHeaders, },
				data: body,
				timeout
			})
				.then((response: any) => response)
				// eslint-disable-next-line consistent-return
				.catch((err: any) => {
					if (returnResponse) {
						return { data: err?.response?.data };
					}
					const statusCode = err?.response?.status || 405;
					let errorMsg = err?.response?.data?.error || err?.response?.statusText || 'Something went wrong';
					if (statusCode === 500 || statusCode === 501 || statusCode === 400) {
						throw err;
					}
					if (statusCode === 403) {
						errorMsg = 'You are unauthorized to perform this action.';
					}
					history.push('/error', {
						statusCode,
						errorMsg
					});
				})
		);
	};

	const putData = ({
		endpoint = '/',
		body = EMPTY_OBJECT,
		baseUrlFromParams,
		customHeaders = EMPTY_OBJECT
	}: {
		endpoint: any;
		queryParams?: any;
		customHeaders?: any;
		body?: any;
		timeout?: any;
		baseUrlFromParams?: string;
	}) => {
		const isACLEndpoint: boolean = merchantName !== MERCHANT_TYPES.MARKETING || checkACLEndpoint(endpoint as string);
		return axios({
			baseURL: isACLEndpoint ? adminBaseUrl : baseUrlFromParams || baseURL,
			method: 'PUT',
			url: endpoint,
			headers: { ...customHeaders, ...cseHeaders() },
			data: body
		})
			.then((response: any) => response)
			.catch((err: any) => {
				const statusCode = err?.response?.status || 405;
				if (statusCode === 500 || statusCode === 501 || statusCode === 400) {
					throw err;
				}
				history.push('/error', {
					statusCode,
					errorMsg: err?.response?.data?.error || 'Something went wrong'
				});
			});
	};

	const getData = ({
		endpoint = '/',
		customHeaders = EMPTY_OBJECT,
		queryParams = EMPTY_OBJECT,
		timeout = DEFAULT_REQUEST_TIMEOUT
	}: {
		endpoint: any;
		customHeaders?: any;
		queryParams?: any;
		timeout?: any;
	}) => {
		const isACLEndpoint: boolean = merchantName !== MERCHANT_TYPES.MARKETING || checkACLEndpoint(endpoint as string);
		const mandatoryHeaders = createRequestHeaders(merchantName);
		return axios({
			method: 'GET',
			baseURL: isACLEndpoint ? adminBaseUrl : baseURL,
			url: endpoint,
			params: queryParams,
			headers: { ...mandatoryHeaders, ...customHeaders },
			timeout
		})
			.then((response: any) => response)
			.catch((err: any) => {
				const statusCode = err?.response?.status || 405;
				if (statusCode === 500 || statusCode === 501 || statusCode === 400) {
					throw err;
				}
				history.push('/error', {
					statusCode,
					errorMsg: err?.response?.data?.error || 'Something went wrong'
				});
			});
	};

	return {
		postData,
		getData,
		putData
	};
};

export default useAjaxProvider;
